import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

const industries = [
  {
    id: 'home-services',
    icon: 'ri-tools-line',
    name: 'Home Services',
    tagline: 'Never miss a booked job again.',
    intro: 'Home service businesses operate in real time. When a call is missed, the opportunity is lost. Technicians in the field cannot respond instantly, and manual follow-up leads to inconsistent booking rates.',
    body: 'AlphaOps AI ensures every inbound call, form, and message is captured and responded to immediately. Our systems qualify leads, schedule jobs, and maintain consistent communication without requiring additional staff.',
    result: 'More booked jobs, fewer missed opportunities, and higher revenue per lead.',
    features: [
      'Instant call capture and response',
      'Automated job scheduling',
      'Lead qualification workflows',
      'Follow-up sequences for unconverted leads',
      'Real-time booking confirmation',
    ],
    image: 'https://public.readdy.ai/ai/img_res/b2274443c6e2c96d9c46e00ac96006cb.jpg',
  },
  {
    id: 'professional-services',
    icon: 'ri-briefcase-line',
    name: 'Professional Services',
    tagline: 'Structured intake. Predictable pipeline.',
    intro: 'Law firms, consultants, and advisory businesses rely on timely intake and structured follow-up. Delays in response reduce trust and decrease conversion rates.',
    body: 'We implement systems that capture inquiries, qualify prospects, and ensure every lead is handled with professionalism and speed. From first contact to consultation booking, your pipeline becomes structured and predictable.',
    result: 'Higher-quality clients, improved intake efficiency, and stronger conversion rates.',
    features: [
      'Automated inquiry capture',
      'Prospect qualification scoring',
      'Consultation booking automation',
      'Professional follow-up sequences',
      'Pipeline visibility and reporting',
    ],
    image: 'https://readdy.ai/api/search-image?query=modern%20professional%20services%20office%20environment%20with%20laptop%20documents%20and%20business%20items%20on%20clean%20desk%2C%20minimal%20business%20photography%20style%2C%20soft%20natural%20light%2C%20clean%20white%20background%2C%20high%20quality%20commercial%20photography%2C%20no%20people&width=700&height=500&seq=indfull002&orientation=landscape',
  },
  {
    id: 'real-estate',
    icon: 'ri-building-line',
    name: 'Real Estate',
    tagline: 'Speed-to-lead wins deals.',
    intro: 'Speed is critical in real estate. Leads lose value within minutes if not engaged. Manual outreach creates delays and inconsistency across teams.',
    body: 'AlphaOps AI enables instant response to new inquiries, automated follow-up across multiple touchpoints, and centralized tracking of every interaction. Your team stays focused on closing while the system handles the rest.',
    result: 'Faster speed-to-lead, increased engagement, and more closed deals.',
    features: [
      'Instant lead response (under 60 seconds)',
      'Multi-channel follow-up automation',
      'Centralized lead tracking',
      'Team routing and assignment',
      'Engagement analytics and reporting',
    ],
    image: 'https://readdy.ai/api/search-image?query=modern%20real%20estate%20property%20architecture%20exterior%20with%20clean%20lines%20and%20minimal%20design%2C%20professional%20architectural%20photography%20style%2C%20soft%20natural%20light%2C%20clean%20white%20sky%20background%2C%20high%20quality%20commercial%20photography%2C%20no%20people&width=700&height=500&seq=indfull003&orientation=landscape',
  },
  {
    id: 'healthcare',
    icon: 'ri-heart-pulse-line',
    name: 'Healthcare',
    tagline: 'Optimized patient intake at scale.',
    intro: 'Healthcare providers face challenges with patient intake, appointment scheduling, and retention. Missed calls and manual coordination create inefficiencies and lost revenue.',
    body: 'We deploy systems that automate patient communication, streamline intake processes, and ensure appointments are consistently booked and confirmed. Your front desk operates at full capacity without the bottlenecks.',
    result: 'Improved patient experience, higher appointment volume, and optimized front-desk operations.',
    features: [
      'Automated patient intake',
      'Appointment scheduling and confirmation',
      'Recall and retention sequences',
      'HIPAA-conscious communication workflows',
      'Front-desk efficiency reporting',
    ],
    image: 'https://readdy.ai/api/search-image?query=modern%20healthcare%20medical%20clinic%20interior%20with%20clean%20equipment%20and%20professional%20medical%20items%20on%20clean%20surface%2C%20minimal%20medical%20photography%20style%2C%20soft%20natural%20light%2C%20clean%20white%20background%2C%20high%20quality%20commercial%20photography%2C%20no%20people&width=700&height=500&seq=indfull004&orientation=landscape',
  },
];

export default function IndustriesPage() {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20dark%20technology%20background%20with%20subtle%20geometric%20patterns%20and%20grid%20lines%2C%20deep%20charcoal%20black%20tones%2C%20minimal%20futuristic%20aesthetic%2C%20very%20dark%20atmosphere%2C%20no%20people%2C%20high%20contrast%20dark%20background&width=1920&height=600&seq=indhero001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/50 to-[#0A0A0A]"></div>
        </div>
        <div className="relative z-10 max-w-[1360px] mx-auto px-8 md:px-16">
          <Link to="/" className="inline-flex items-center gap-2 text-[#707070] text-sm hover:text-white transition-colors mb-8 cursor-pointer">
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-block w-8 h-px bg-[#38C6F5]"></span>
            <span className="text-[#38C6F5] text-xs uppercase tracking-[3px]">Industries We Serve</span>
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extralight text-white leading-tight max-w-[800px] mb-6">
            Built for businesses where{' '}
            <span className="font-semibold">speed drives revenue.</span>
          </h1>
          <p className="text-[#A0A0A0] text-lg leading-relaxed max-w-[580px]">
            AlphaOps AI is built for service-driven businesses where operational efficiency directly impacts revenue. Each industry we serve faces a common challenge — we solve it.
          </p>
        </div>
      </section>

      {/* Industries */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1360px] mx-auto px-8 md:px-16 flex flex-col gap-24">
          {industries.map((industry, i) => (
            <div
              key={industry.id}
              id={industry.id}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                i % 2 === 1 ? 'lg:flex lg:flex-row-reverse' : ''
              }`}
            >
              {/* Image */}
              <div className="w-full h-[400px] md:h-[500px] rounded-2xl overflow-hidden flex-shrink-0 lg:w-1/2">
                <img
                  src={industry.image}
                  alt={industry.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Content */}
              <div className={`flex-1 ${i % 2 === 1 ? 'lg:pr-12' : 'lg:pl-0'}`}>
                <div className="w-12 h-12 flex items-center justify-center bg-[#38C6F5]/10 rounded-xl mb-6">
                  <i className={`${industry.icon} text-[#38C6F5] text-2xl`}></i>
                </div>
                <h2 className="text-3xl md:text-4xl font-light text-white mb-2">{industry.name}</h2>
                <p className="text-[#38C6F5] text-sm font-medium mb-6">{industry.tagline}</p>
                <p className="text-[#A0A0A0] text-base leading-relaxed mb-4">{industry.intro}</p>
                <p className="text-[#A0A0A0] text-base leading-relaxed mb-8">{industry.body}</p>

                {/* Features */}
                <div className="mb-8">
                  <p className="text-white text-xs uppercase tracking-widest mb-4">What We Implement</p>
                  <ul className="flex flex-col gap-3">
                    {industry.features.map((f) => (
                      <li key={f} className="flex items-center gap-3 text-[#A0A0A0] text-sm">
                        <div className="w-5 h-5 flex items-center justify-center text-[#38C6F5] flex-shrink-0">
                          <i className="ri-check-line text-base"></i>
                        </div>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Result */}
                <div className="border border-[#38C6F5]/20 bg-[#38C6F5]/5 rounded-xl p-5">
                  <p className="text-[#38C6F5] text-xs uppercase tracking-widest mb-2">Result</p>
                  <p className="text-white text-sm leading-relaxed">{industry.result}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Unified Advantage */}
      <section className="py-24 md:py-32 bg-[#F5F5F0]">
        <div className="max-w-[1360px] mx-auto px-8 md:px-16 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="inline-block w-8 h-px bg-[#0A0A0A]"></span>
            <span className="text-[#0A0A0A] text-xs uppercase tracking-[3px]">Unified Advantage</span>
            <span className="inline-block w-8 h-px bg-[#0A0A0A]"></span>
          </div>
          <h2 className="text-4xl md:text-5xl font-extralight text-[#0A0A0A] leading-tight mb-6 max-w-[640px] mx-auto">
            Across every industry, the outcome remains{' '}
            <span className="font-semibold">consistent.</span>
          </h2>
          <p className="text-[#555555] text-base leading-relaxed max-w-[520px] mx-auto mb-16">
            AlphaOps AI transforms how service businesses operate by turning fragmented workflows into unified, intelligent systems.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            {[
              { icon: 'ri-timer-flash-line', label: 'Faster Response Times', desc: 'Under 60 seconds to every inbound lead' },
              { icon: 'ri-user-follow-line', label: 'Improved Lead Handling', desc: 'Every inquiry captured and qualified' },
              { icon: 'ri-line-chart-line', label: 'Increased Conversion', desc: 'Higher rates across every touchpoint' },
              { icon: 'ri-shield-check-line', label: 'Operational Control', desc: 'Full visibility into your pipeline' },
            ].map((item) => (
              <div key={item.label} className="bg-white rounded-2xl p-6 text-left">
                <div className="w-10 h-10 flex items-center justify-center bg-[#0A0A0A] rounded-lg mb-4">
                  <i className={`${item.icon} text-[#38C6F5] text-lg`}></i>
                </div>
                <h3 className="text-[#0A0A0A] text-sm font-semibold mb-2">{item.label}</h3>
                <p className="text-[#777777] text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <Link
            to="/#cta"
            className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white text-sm font-medium px-10 py-4 rounded-full hover:bg-[#38C6F5] hover:text-black transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            Schedule Your Consultation
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
